/**
 * Te Pou Mataaho — canonical reference data.
 *
 * Source-of-truth for the framework's structural shape. Sourced from
 * `docs/Sample analysis.xlsx`, `docs/Risk and Protective Factors guide to mapping.docx`,
 * and `docs/Brief description.docx`.
 *
 * All data is read-only. The framework is not user-editable at runtime —
 * kaimahi record assessments against existing subdomains, but cannot add
 * new ones. Framework changes require a code change, an ADR amendment,
 * and a SQL migration to keep `framework.sql` in sync.
 *
 * See docs/ADR-005-framework-data-model.md for the full rationale.
 */

import type {
  AlertDefinition,
  CycleLabel,
  Domain,
  FactorState,
  FactorStateKind,
  NotAssessedReason,
  Pou,
  Subdomain,
} from "./types";

// -------- Pou (4) --------

export const POUS: readonly Pou[] = [
  {
    id: "ihorangi",
    nameMi: "Te Pou Ihorangi",
    description:
      "Represents the spiritual and cultural beginnings of whānau originating from the spiritual.",
  },
  {
    id: "hineahuone",
    nameMi: "Te Pou o Hineahuone",
    description:
      "Provides us with the knowledge of the connection between ourselves and others through our kinship ties and whakapapa.",
  },
  {
    id: "aoturoa",
    nameMi: "Te Pou Aotūroa",
    description:
      "Recognises the world around us and all the different influences both the negative and positive that contribute to our development.",
  },
  {
    id: "hinetitama",
    nameMi: "Te Pou Hinetītama",
    description:
      "Tūrangawaewae is the place you call home, your connection through the whenua.",
  },
] as const;

// -------- Domains (6) --------

export const DOMAINS: readonly Domain[] = [
  { id: "wider-connections", nameEn: "Wider connections" },
  { id: "personal-connections", nameEn: "Personal connections" },
  { id: "education", nameEn: "Education" },
  { id: "health", nameEn: "Health" },
  { id: "financial-stability", nameEn: "Financial stability" },
  { id: "housing", nameEn: "Housing" },
] as const;

// -------- Subdomains (18) --------
// 18-subdomain shape per ADR-005 §5b (Enid 2026-05-11): added 'poverty'
// under Economic Stability, renamed 'parent-education' → 'adult-literacy'.

export const SUBDOMAINS: readonly Subdomain[] = [
  // Wider Connections (3)
  {
    id: "cultural-connection",
    domainId: "wider-connections",
    pouId: "ihorangi",
    nameEn: "Cultural connection",
    toCOutcome: "Improved cultural connections",
  },
  {
    id: "spiritual-connection",
    domainId: "wider-connections",
    pouId: "ihorangi",
    nameEn: "Spiritual connection",
    toCOutcome: "Increased sense of dignity and mana",
  },
  {
    id: "social-connection",
    domainId: "wider-connections",
    pouId: "hineahuone",
    nameEn: "Social connection",
    toCOutcome: "Improved social participation and belonging",
  },
  // Personal Connections (4)
  {
    id: "relationships",
    domainId: "personal-connections",
    pouId: "hineahuone",
    nameEn: "Relationships (in whānau)",
    toCOutcome: "Strengthened whānau relationships and stability",
  },
  {
    id: "support-networks",
    domainId: "personal-connections",
    pouId: "hineahuone",
    nameEn: "Support networks",
    toCOutcome: "Strengthened social support networks",
  },
  {
    id: "home-environment",
    domainId: "personal-connections",
    pouId: "aoturoa",
    nameEn: "Home environment",
    toCOutcome: "Healthy and safe living circumstances",
  },
  {
    id: "parenting",
    domainId: "personal-connections",
    pouId: "aoturoa",
    nameEn: "Parenting",
    toCOutcome: "Safe and nurturing parenting",
  },
  // Education (2) — renamed parent-education → adult-literacy per Sue 2026-05-11
  {
    id: "adult-literacy",
    domainId: "education",
    pouId: "aoturoa",
    nameEn: "Adult literacy",
    toCOutcome: "Increased adult literacy",
  },
  {
    id: "child-education",
    domainId: "education",
    pouId: "aoturoa",
    nameEn: "Child education",
    toCOutcome: "Tamariki attending kura/ECE and learning",
  },
  // Health (5)
  {
    id: "mental-health",
    domainId: "health",
    pouId: "aoturoa",
    nameEn: "Mental health",
    toCOutcome: "Strong mental health and mental resilience. Reduced self-harm",
  },
  {
    id: "physical-health",
    domainId: "health",
    pouId: "aoturoa",
    nameEn: "Physical health",
    toCOutcome: "Physically healthy and well",
  },
  {
    id: "emotional-health-behaviour",
    domainId: "health",
    pouId: "aoturoa",
    nameEn: "Emotional health and behaviour",
    toCOutcome:
      "Increased resilience. Reduced impact of grief and loss. Increased whānau healing",
  },
  {
    id: "addictions",
    domainId: "health",
    pouId: "aoturoa",
    nameEn: "Addictions",
    toCOutcome: "Addiction free",
  },
  {
    id: "disability",
    domainId: "health",
    pouId: "aoturoa",
    nameEn: "Disability",
    toCOutcome:
      "Reduced social impact of disability and increased opportunities",
  },
  // Financial Stability (3) — 18th subdomain (id 'poverty') displays as 'Access to kai and essentials' per Sue 2026-06-30
  {
    id: "employment",
    domainId: "financial-stability",
    pouId: "hinetitama",
    nameEn: "Employment",
    toCOutcome: "Sustained employment and reduced benefit dependency",
  },
  {
    id: "financial-stability",
    domainId: "financial-stability",
    pouId: "hinetitama",
    nameEn: "Financial stability",
    toCOutcome: "Improved financial stability and independence",
  },
  {
    // id kept as 'poverty' for data stability; renamed for display to
    // 'Access to kai and essentials' (Sue, 2026-06-30). te reo: Des to confirm.
    id: "poverty",
    domainId: "financial-stability",
    pouId: "hinetitama",
    nameEn: "Access to kai and essentials",
    toCOutcome: "Reliable access to kai and essentials",
  },
  // Housing (1)
  {
    id: "accommodation",
    domainId: "housing",
    pouId: "hinetitama",
    nameEn: "Accommodation",
    toCOutcome: "Living in healthy affordable homes",
  },
] as const;

// -------- Factor states (4) --------

/**
 * Te reo names taken from the Population view sheet of Sample analysis.xlsx,
 * with macrons restored per CLAUDE.md conventions. AI-drafted; needs
 * fluent-speaker review before user testing — see ADR-005 §3 Q3.
 *
 * "Not assessed" has no source-document te reo name. Recommend coining one
 * with the kuia reviewer. Until then, English label is used in both locales.
 */
export const FACTOR_STATES: Readonly<Record<FactorStateKind, FactorState>> = {
  risk: {
    kind: "risk",
    teReoName: "Te Korenga",
    nameEn: "Risk factor",
  },
  change: {
    kind: "change",
    teReoName: "Te Aranga Ake / Te Ohonga Ake",
    nameEn: "Change factor",
  },
  protective: {
    kind: "protective",
    teReoName: "Te Mōhiotanga / Te Māramatanga",
    nameEn: "Protective factor",
  },
  "not-assessed": {
    kind: "not-assessed",
    teReoName: null,
    nameEn: "Not assessed",
  },
} as const;

// -------- Not assessed reasons (3) --------

export const NOT_ASSESSED_REASONS: readonly NotAssessedReason[] = [
  "not-applicable",
  "declined",
  "not-yet-assessed",
] as const;

// -------- Cycle labels (controlled vocabulary) --------

export const CYCLE_LABELS: readonly CycleLabel[] = [
  { id: "entry", nameEn: "Entry" },
  { id: "midpoint", nameEn: "Midpoint" },
  { id: "exit", nameEn: "Exit" },
  { id: "follow-up", nameEn: "Follow-up" },
] as const;

// -------- Alert definitions --------

export const ALERTS: readonly AlertDefinition[] = [
  {
    id: "homeless",
    nameEn: "Homeless",
    description:
      "Whānau in housing risk — surfaced as an alert when the profile is opened.",
    subdomainId: "accommodation",
    triggerState: "risk",
    severity: "urgent",
  },
  {
    // id kept stable; relabelled with the subdomain (Sue, 2026-06-30).
    id: "experiencing-poverty",
    nameEn: "Going without kai or essentials",
    description:
      "Whānau without reliable access to kai or essentials — surfaced as an alert when the profile is opened.",
    subdomainId: "poverty",
    triggerState: "risk",
    severity: "urgent",
  },
] as const;

// -------- Lookup helpers --------

/** Find a Pou by ID. Returns undefined if the ID isn't recognised. */
export function findPou(id: string): Pou | undefined {
  return POUS.find((p) => p.id === id);
}

/** Find a domain by ID. */
export function findDomain(id: string): Domain | undefined {
  return DOMAINS.find((d) => d.id === id);
}

/** Find a subdomain by ID. */
export function findSubdomain(id: string): Subdomain | undefined {
  return SUBDOMAINS.find((s) => s.id === id);
}

/** All subdomains for a given domain, in canonical order. */
export function subdomainsForDomain(
  domainId: Domain["id"],
): readonly Subdomain[] {
  return SUBDOMAINS.filter((s) => s.domainId === domainId);
}

/** All subdomains for a given Pou, in canonical order. */
export function subdomainsForPou(pouId: Pou["id"]): readonly Subdomain[] {
  return SUBDOMAINS.filter((s) => s.pouId === pouId);
}

// -------- Invariants (compile-time hints) --------

/**
 * The framework has 18 subdomains. Confirmed by Enid 2026-05-11 (the 18th is
 * 'poverty'). If this assertion fails, the data has drifted from the canonical
 * shape — review ADR-005 §5b and reconcile.
 */
export const SUBDOMAIN_COUNT = 18 as const;
if (SUBDOMAINS.length !== SUBDOMAIN_COUNT) {
  throw new Error(
    `[WFP framework] Expected ${SUBDOMAIN_COUNT} subdomains, found ${SUBDOMAINS.length}. ` +
      "See docs/ADR-005-framework-data-model.md.",
  );
}
